/**
 * csv.js — CSV building + download (pure builder, thin DOM trigger).
 *
 * Exports stay in ENGLISH and in canonical units regardless of the UI language,
 * matching the DailyOps convention: a CSV is an interchange format destined for
 * Excel or an accountant, not a localized screen.
 */

/**
 * Quote one CSV field.
 *
 * Beyond the obvious quote/comma/newline escaping, this prefixes a leading
 * `=`, `+`, `-` or `@` with an apostrophe. Excel and Sheets interpret those as
 * the start of a formula, so a ticket literally named "=cmd|..." becomes a
 * live formula on open — the CSV-injection problem. Ticket and item names come
 * from customer-facing input, so they must not be trusted here.
 */
export function csvCell(value) {
  if (value == null) return '';
  const isNumber = typeof value === 'number' && Number.isFinite(value);
  let s = String(value);
  // Only text needs the guard — a real number can't be a formula, and escaping
  // it would land -5 in Excel as text and break every numeric column.
  if (!isNumber && /^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  if (/["\n\r,]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Build a CSV document from column definitions and rows.
 *
 * @param {Array<{key:string,label:string,get?:Function}>} columns
 * @param {object[]} rows
 * @returns {string} CRLF-delimited CSV (RFC 4180)
 */
export function buildCsv(columns, rows) {
  const cols = columns || [];
  const header = cols.map(c => csvCell(c.label ?? c.key)).join(',');
  const body = (rows || []).map(row =>
    cols.map(c => csvCell(c.get ? c.get(row) : row?.[c.key])).join(',')
  );
  return [header, ...body].join('\r\n');
}

/** `prefix_2026-08-30_1432.csv` — sorts chronologically in a downloads folder. */
export function csvFilename(prefix, date = new Date()) {
  const p = n => String(n).padStart(2, '0');
  const stamp = `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`
    + `_${p(date.getHours())}${p(date.getMinutes())}`;
  return `${prefix}_${stamp}.csv`;
}

/**
 * Trigger a browser download of `content`.
 *
 * The ﻿ BOM makes Excel read the file as UTF-8 — without it, any Telugu
 * item name (or even a curly apostrophe) opens as mojibake on Windows.
 */
export function downloadCsv(filename, content) {
  const blob = new Blob([`﻿${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick — revoking synchronously can cancel the download
  // in some browsers before it has read the blob.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Build + download in one call. */
export function exportCsv(prefix, columns, rows, date = new Date()) {
  downloadCsv(csvFilename(prefix, date), buildCsv(columns, rows));
}
